<template>
  <div class="musubi-page musubi-page--tps">
    <header class="musubi-page__head">
      <h1>TPS/CTPS screening</h1>
      <p class="text-muted">
        Screen lead phone numbers against the DMA-licensed Telephone Preference Service
        (individuals and sole traders) and Corporate TPS registers before any unsolicited
        marketing call. The register file refreshes monthly; verdicts show on each lead as
        an advisory "OK to call" or "do not cold call" badge.
      </p>
    </header>

    <div v-if="loading" class="text-muted">Loading…</div>
    <div v-else-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <template v-else>
      <MusubiSectionCard title="Register health" icon="phone" class="tps-card-gap">
        <template #actions>
          <span v-if="configured" class="tps-status tps-status--ok"><CygnusIcon name="check-circle" class="me-1" />Download configured</span>
          <span v-else class="tps-status tps-status--off">Download not configured</span>
        </template>
        <div class="tps-stats">
          <div class="tps-stat">
            <div class="tps-stat__label">TPS (individuals / sole traders)</div>
            <div class="tps-stat__rows">{{ (register.tps && register.tps.rows || 0).toLocaleString() }} numbers</div>
            <div class="tps-stat__meta">File {{ fmtFileDate(register.tps && register.tps.file_date) }}</div>
          </div>
          <div class="tps-stat">
            <div class="tps-stat__label">CTPS (corporates)</div>
            <div class="tps-stat__rows">{{ (register.ctps && register.ctps.rows || 0).toLocaleString() }} numbers</div>
            <div class="tps-stat__meta">File {{ fmtFileDate(register.ctps && register.ctps.file_date) }}</div>
          </div>
        </div>
        <p class="tps-lastrun">Last ingest: {{ lastIngestAt ? fmtDate(lastIngestAt) : 'never' }}</p>
        <div class="tps-actions">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="ingesting" @click="runIngest">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="ingesting" />Run ingest now
          </button>
          <div v-if="ingestResult" :class="['tps-actions__msg', ingestResult.ok ? 'is-ok' : 'is-error']">{{ ingestResult.message }}</div>
        </div>
      </MusubiSectionCard>

      <MusubiSectionCard title="DMA download" icon="server" class="tps-card-gap"
                         subtitle="Where the monthly register files are downloaded from">
        <div class="tps-field">
          <div class="tps-field__head"><span class="tps-field__label">TPS file URL</span></div>
          <input v-model="form.tps_file_url" type="text" class="form-control form-control-sm" placeholder="https://…/tps-latest.csv" >
          <p class="tps-field__help">The DMA download URL for the TPS (individuals / sole traders) monthly file.</p>
        </div>

        <div class="tps-field">
          <div class="tps-field__head"><span class="tps-field__label">CTPS file URL</span></div>
          <input v-model="form.ctps_file_url" type="text" class="form-control form-control-sm" placeholder="https://…/ctps-latest.csv" >
          <p class="tps-field__help">The DMA download URL for the CTPS (corporates) monthly file.</p>
        </div>

        <div class="tps-field">
          <div class="tps-field__head">
            <span class="tps-field__label">Download credential</span>
            <span v-if="credentialSet" class="tps-field__hint">set</span>
          </div>
          <input
            v-model="form.file_credentials"
            type="password"
            autocomplete="new-password"
            class="form-control form-control-sm"
            :placeholder="credentialMasked || 'HTTP Basic user:pass or bearer token'"
            :disabled="clearCredential"
          >
          <p class="tps-field__help">Sent as the Authorization header when downloading. Encrypted at rest, never shown back in full. Leave blank to keep the current credential.</p>
          <label v-if="credentialSet" class="tps-clear">
            <input v-model="clearCredential" type="checkbox" > Remove the stored credential
          </label>
        </div>
      </MusubiSectionCard>

      <div class="tps-save">
        <button type="button" class="btn btn-primary btn-sm" :disabled="saving" @click="save">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="saving" /><span v-else>Save</span>
        </button>
        <div v-if="saveMessage" :class="['tps-save__msg', saveOk ? 'is-ok' : 'is-error']">{{ saveMessage }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

const ENDPOINT = '/api/v1/admin/tps-screening';

export default {
  name: 'AdminTpsScreening',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loading: true,
      saving: false,
      ingesting: false,
      error: null,
      configured: false,
      credentialSet: false,
      credentialMasked: '',
      clearCredential: false,
      lastIngestAt: null,
      register: { tps: { rows: 0, file_date: null }, ctps: { rows: 0, file_date: null } },
      form: {
        tps_file_url: '',
        ctps_file_url: '',
        file_credentials: '',
      },
      ingestResult: null,
      saveMessage: '',
      saveOk: false,
    };
  },
  mounted() { this.load(); },
  methods: {
    applyState(p) {
      this.configured = !!p.configured;
      this.credentialSet = !!p.credential_set;
      this.credentialMasked = p.credential_masked || '';
      this.lastIngestAt = p.last_ingest_at || null;
      this.register = p.register || this.register;
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get(ENDPOINT);
        const p = res?.data ?? res;
        this.applyState(p);
        this.form = {
          tps_file_url: p.tps_file_url || '',
          ctps_file_url: p.ctps_file_url || '',
          file_credentials: '',
        };
        this.clearCredential = false;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load TPS/CTPS screening.';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.saveMessage = '';
      this.saveOk = false;
      const body = {
        tps_file_url: this.form.tps_file_url || '',
        ctps_file_url: this.form.ctps_file_url || '',
      };
      if (this.clearCredential) body.clear_credentials = true;
      else if (this.form.file_credentials) body.file_credentials = this.form.file_credentials;

      try {
        const res = await this.api.put(ENDPOINT, body);
        const p = res?.data ?? res;
        if (p?.success) {
          this.saveOk = true;
          this.saveMessage = 'Saved.';
          this.applyState(p);
          this.form.file_credentials = '';
          this.clearCredential = false;
        } else {
          this.saveMessage = p?.error || 'Save failed.';
        }
      } catch (e) {
        this.saveMessage = e?.response?.data?.error || e?.message || 'Save failed.';
      } finally {
        this.saving = false;
      }
    },
    async runIngest() {
      this.ingesting = true;
      this.ingestResult = null;
      try {
        const res = await this.api.post(`${ENDPOINT}/ingest`, {});
        const p = res?.data ?? res;
        this.applyState(p);
        const r = p?.result || {};
        if (r.skipped === 'needs_config') {
          this.ingestResult = { ok: false, message: 'Download not configured — set the file URLs and credential first.' };
        } else {
          const parts = Object.entries(r.lists || {}).map(([list, s]) =>
            s.error ? `${list.toUpperCase()}: ${s.error}` : `${list.toUpperCase()}: ${(s.ingested || 0).toLocaleString()} ingested`);
          this.ingestResult = { ok: true, message: parts.length ? parts.join(' · ') : 'Ingest ran.' };
        }
      } catch (e) {
        this.ingestResult = { ok: false, message: e?.response?.data?.error || e?.message || 'Ingest failed.' };
      } finally {
        this.ingesting = false;
      }
    },
    fmtFileDate(fd) {
      if (!fd) return '—';
      const s = String(fd);
      return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : s;
    },
    fmtDate(ts) { return ts ? new Date(ts * 1000).toLocaleString() : '—'; },
  },
};
</script>

<style scoped>
.musubi-page--tps { padding: var(--m-space-6, 32px); max-width: 720px; }
.tps-card-gap { margin-top: 18px; }

.tps-status { font-size: 0.76rem; font-weight: 600; }
.tps-status--ok { color: var(--m-status-success, #10B981); }
.tps-status--off { color: var(--m-text-tertiary); }

.tps-stats { display: flex; gap: 24px; flex-wrap: wrap; }
.tps-stat { flex: 1 1 220px; }
.tps-stat__label { font-size: 0.82rem; font-weight: 500; color: var(--m-text-primary); }
.tps-stat__rows { font-size: 1.3rem; font-weight: 700; color: var(--m-text-primary); margin-top: 2px; }
.tps-stat__meta { font-size: 0.76rem; color: var(--m-text-secondary); }
.tps-lastrun { font-size: 0.78rem; color: var(--m-text-secondary); margin: 12px 0 0; }

.tps-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
.tps-field:last-child { margin-bottom: 0; }
.tps-field__head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.tps-field__label { font-size: 0.85rem; font-weight: 500; color: var(--m-text-primary); }
.tps-field__hint { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); }
.tps-field__help { font-size: 0.76rem; color: var(--m-text-secondary); margin: 2px 0 0; }

.tps-clear { display: flex; align-items: center; gap: 6px; font-size: 0.78rem; color: var(--m-text-secondary); margin-top: 4px; }
.tps-clear input { margin: 0; }

.tps-actions { display: flex; align-items: center; gap: 10px; margin-top: 14px; }
.tps-actions__msg { font-size: 0.82rem; }
.tps-actions__msg.is-ok { color: var(--m-status-success, #10B981); }
.tps-actions__msg.is-error { color: var(--m-status-danger, #EF4444); }

.tps-save { display: flex; align-items: center; gap: 10px; margin-top: 18px; }
.tps-save__msg { font-size: 0.85rem; }
.tps-save__msg.is-ok { color: var(--m-status-success, #10B981); }
.tps-save__msg.is-error { color: var(--m-status-danger, #EF4444); }
</style>
